import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FaTruck, FaUserCheck } from 'react-icons/fa';
import { AdminNameCell, AdminRowActions, rowClickHandler } from '../admin/AdminRowCells';
import AddressSuggestInput from './AddressSuggestInput';
import {
  buildInventoryOwnerPayload,
  createSupplier,
  deleteSupplier,
  fetchSupplierDetail,
  fetchSuppliersByOwner,
  getInventoryOwnerContext,
  updateSupplier,
} from './inventoryApi';
import { getInventoryOwnerRequiredMessage } from './inventoryEntity';
import { formatMobileDisplayOrFallback, sanitizeMobileInput, validateMobileNumber } from './formatDisplay';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';
import './css/Inventory.css';
import './css/Menu.css';
import './css/OrdersTablePagination.css';
import './css/Receivables.css';
import { confirmAction } from './ui/feedback';

const initialForm = {
  supplierName: '',
  contactPerson: '',
  email: '',
  altNo: '',
  mobile: '',
  address: '',
  isActive: true,
};

function isSupplierActive(row) {
  if (!row || typeof row !== 'object') return false;
  const a = row.isActive ?? row.Is_Active;
  return a === true || a === 1 || a === '1';
}

function supplierRowMatchesSearch(row, query) {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;
  const activeLabel = isSupplierActive(row) ? 'yes' : 'no';
  const fields = [
    row?.supplierId,
    row?.Supplier_Id,
    row?.supplierName,
    row?.Supplier_Name,
    row?.contactPerson,
    row?.Contact_Person,
    row?.mobile,
    row?.Mobile,
    row?.email,
    row?.Email,
    row?.altNo,
    row?.Alt_No,
    row?.address,
    row?.Address,
    activeLabel,
  ];
  return fields.some((v) => v != null && String(v).toLowerCase().includes(q));
}

function getSupplierSortValue(row, sortKey) {
  switch (sortKey) {
    case 'supplierId':
      return Number(row.supplierId ?? row.Supplier_Id) || 0;
    case 'supplierName':
      return str(row.supplierName ?? row.Supplier_Name);
    case 'contactPerson':
      return str(row.contactPerson ?? row.Contact_Person);
    case 'mobile':
      return str(row.mobile ?? row.Mobile);
    case 'email':
      return str(row.email ?? row.Email);
    case 'altNo':
      return str(row.altNo ?? row.Alt_No);
    case 'address':
      return str(row.address ?? row.Address);
    case 'isActive':
      return isSupplierActive(row) ? 1 : 0;
    default:
      return '';
  }
}

function buildFormFromSupplierRow(row) {
  if (!row || typeof row !== 'object') return { ...initialForm };
  const active = isSupplierActive(row);
  return {
    supplierName: String(row.supplierName ?? row.Supplier_Name ?? ''),
    contactPerson: String(row.contactPerson ?? row.Contact_Person ?? ''),
    email: String(row.email ?? row.Email ?? ''),
    altNo: sanitizeMobileInput(row.altNo ?? row.Alt_No ?? ''),
    mobile: sanitizeMobileInput(row.mobile ?? row.Mobile ?? ''),
    address: String(row.address ?? row.Address ?? ''),
    isActive: active,
  };
}

const InventorySupplier = ({ onSupplierCreated }) => {
  const navigate = useNavigate();
  const owner = getInventoryOwnerContext();
  const { ownerId } = owner;
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const createdById = Number(user?.userId || user?.id || user?.User_Id || 0);
  const updatedById = createdById;

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [supplierDetailLoading, setSupplierDetailLoading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [listMessage, setListMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suppliersPage, setSuppliersPage] = useState(1);
  const [suppliersPageSize, setSuppliersPageSize] = useState(10);
  const [suppliersSort, setSuppliersSort] = useState(null);
  const [supplierListSearch, setSupplierListSearch] = useState('');
  const [supplierStatFilter, setSupplierStatFilter] = useState('all');
  /** When editing, whether supplier was active when modal opened (hides Active field if true). */
  const [editStartedActive, setEditStartedActive] = useState(false);

  const resetForm = () => {
    setForm({ ...initialForm });
    setEditingSupplierId(null);
    setSupplierDetailLoading(false);
    setEditStartedActive(false);
  };

  const fetchSuppliers = useCallback(async () => {
    const ctx = getInventoryOwnerContext();
    if (!ctx.ownerId) {
      setSuppliers([]);
      return;
    }
    try {
      setSuppliersLoading(true);
      const rows = await fetchSuppliersByOwner(ctx);
      setSuppliers(Array.isArray(rows) ? rows : []);
      setListMessage('');
    } catch (err) {
      console.error(
        'Failed to load suppliers:',
        err?.response?.status,
        err?.response?.data ?? err?.message
      );
      setSuppliers([]);
      setListMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not load suppliers.'
      );
    } finally {
      setSuppliersLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    setSuppliersPage(1);
    setSupplierListSearch('');
    setSupplierStatFilter('all');
  }, [ownerId]);

  const supplierListSearchQuery = supplierListSearch.trim();

  const toggleSupplierStatFilter = (next) => {
    setSupplierStatFilter((prev) => (prev === next ? 'all' : next));
    setSuppliersPage(1);
  };

  const filteredSuppliers = useMemo(
    () =>
      suppliers.filter((row) => {
        if (!supplierRowMatchesSearch(row, supplierListSearchQuery)) return false;
        if (supplierStatFilter === 'active') return isSupplierActive(row);
        return true;
      }),
    [suppliers, supplierListSearchQuery, supplierStatFilter]
  );

  useEffect(() => {
    setSuppliersPage(1);
  }, [supplierListSearchQuery, supplierStatFilter]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredSuppliers.length / suppliersPageSize));
    setSuppliersPage((p) => Math.min(p, tp));
  }, [filteredSuppliers.length, suppliersPageSize]);

  const sortedSuppliers = useMemo(
    () => sortList(filteredSuppliers, suppliersSort, getSupplierSortValue),
    [filteredSuppliers, suppliersSort]
  );

  const supplierStats = useMemo(() => {
    const active = suppliers.filter((row) => isSupplierActive(row)).length;
    return { total: suppliers.length, active };
  }, [suppliers]);

  const { handleSortAsc: handleSuppliersSortAsc, handleSortDesc: handleSuppliersSortDesc } =
    buildSortHandlers(setSuppliersSort, () => setSuppliersPage(1));

  const suppliersTotalPages = Math.max(1, Math.ceil(sortedSuppliers.length / suppliersPageSize));
  const suppliersPageSafe = Math.min(Math.max(1, suppliersPage), suppliersTotalPages);
  const suppliersPageStart = (suppliersPageSafe - 1) * suppliersPageSize;
  const pagedSuppliers = sortedSuppliers.slice(suppliersPageStart, suppliersPageStart + suppliersPageSize);

  const handleSuppliersPageChange = (nextPage) => {
    setSuppliersPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSuppliersPageSizeChange = (nextSize) => {
    setSuppliersPageSize(nextSize);
    setSuppliersPage(1);
  };

  const handleOpen = () => {
    setSubmitError('');
    setEditingSupplierId(null);
    setSupplierDetailLoading(false);
    setEditStartedActive(false);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = async (row) => {
    const id = row.supplierId ?? row.Supplier_Id ?? null;
    if (!id) return;

    setSubmitError('');
    setForm(buildFormFromSupplierRow(row));
    setEditStartedActive(isSupplierActive(row));
    setEditingSupplierId(id);
    setSupplierDetailLoading(true);
    setShowModal(true);

    try {
      const detail = await fetchSupplierDetail(id, owner);
      if (detail) {
        setForm(buildFormFromSupplierRow(detail));
        setEditStartedActive(isSupplierActive(detail));
      }
    } catch (err) {
      console.error(
        'Failed to load supplier:',
        err?.response?.status,
        err?.response?.data ?? err?.message
      );
      setSubmitError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not load latest supplier from server; values shown are from the list.'
      );
    } finally {
      setSupplierDetailLoading(false);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!supplierId) return;
    const ok = await confirmAction({
      title: 'Delete supplier?',
      message: 'Delete this supplier?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      const data = await deleteSupplier(supplierId, owner);
      await fetchSuppliers();
      alert(data?.message || 'Supplier deleted');
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data?.error || 'Failed to delete supplier.');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSubmitError('');
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === 'mobile' || name === 'altNo'
        ? sanitizeMobileInput(value)
        : type === 'checkbox'
          ? checked
          : value;
    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!ownerId) {
      setSubmitError(getInventoryOwnerRequiredMessage());
      return;
    }
    if (!createdById) {
      setSubmitError('Please sign in again.');
      return;
    }
    if (!form.supplierName.trim()) {
      setSubmitError('Supplier name is required.');
      return;
    }
    if (!form.mobile.trim()) {
      setSubmitError('Mobile is required.');
      return;
    }
    const mobileError = validateMobileNumber(form.mobile, { required: true, label: 'Mobile' });
    if (mobileError) {
      setSubmitError(mobileError);
      return;
    }
    if (form.altNo.trim()) {
      const altError = validateMobileNumber(form.altNo, {
        required: true,
        label: 'Alternate number',
      });
      if (altError) {
        setSubmitError(altError);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      if (editingSupplierId) {
        const payload = {
          ...buildInventoryOwnerPayload(owner),
          supplierName: form.supplierName.trim(),
          contactPerson: form.contactPerson.trim(),
          email: form.email.trim(),
          altNo: form.altNo.trim(),
          mobile: form.mobile.trim(),
          address: form.address.trim(),
          isActive: editStartedActive ? 1 : form.isActive ? 1 : 0,
          updatedById,
        };
        const data = await updateSupplier(editingSupplierId, payload, owner);
        await fetchSuppliers();
        handleClose();
        alert(data?.message || 'Supplier updated');
      } else {
        const payload = {
          ...buildInventoryOwnerPayload(owner),
          supplierName: form.supplierName.trim(),
          contactPerson: form.contactPerson.trim(),
          email: form.email.trim(),
          altNo: form.altNo.trim(),
          mobile: form.mobile.trim(),
          address: form.address.trim(),
          isActive: 1,
          createdById,
        };
        const data = await createSupplier(payload, owner);
        await fetchSuppliers();
        handleClose();
        const created = data?.supplier ?? data?.Supplier ?? null;
        if (typeof onSupplierCreated === 'function' && created) {
          onSupplierCreated(created);
        }
        alert(data?.message || 'Supplier created');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (editingSupplierId ? 'Failed to update supplier.' : 'Failed to create supplier.');
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ownerId) {
    return (
      <div className="inventory-supplier-panel">
        <p className="inventory-status">{getInventoryOwnerRequiredMessage()}</p>
      </div>
    );
  }

  const colCount = 8;

  return (
    <div className="inventory-supplier-panel">
      {listMessage ? <p className="inventory-status">{listMessage}</p> : null}

      <div className="purchase-stats-grid purchase-stats-grid--supplier sales-stats-grid">
        <button
          type="button"
          className={`purchase-stat-card sales-stat-card sales-stat-card--primary${supplierStatFilter === 'all' ? ' purchase-stat-card--active' : ''}`}
          onClick={() => toggleSupplierStatFilter('all')}
          aria-pressed={supplierStatFilter === 'all'}
        >
          <div className="sales-stat-card__top">
            <span className="sales-stat-icon-wrap" aria-hidden="true">
              <FaTruck className="sales-stat-icon" />
            </span>
            <span className="sales-stat-label">Total suppliers</span>
          </div>
          <span className="sales-stat-value purchase-stat-value--primary">{supplierStats.total}</span>
        </button>
        <button
          type="button"
          className={`purchase-stat-card sales-stat-card sales-stat-card--success${supplierStatFilter === 'active' ? ' purchase-stat-card--active' : ''}`}
          onClick={() => toggleSupplierStatFilter('active')}
          aria-pressed={supplierStatFilter === 'active'}
        >
          <div className="sales-stat-card__top">
            <span className="sales-stat-icon-wrap" aria-hidden="true">
              <FaUserCheck className="sales-stat-icon" />
            </span>
            <span className="sales-stat-label">Active suppliers</span>
          </div>
          <span className="sales-stat-value purchase-stat-value--success">{supplierStats.active}</span>
        </button>
      </div>

      <div className="purchase-content-card">
      <div className="inventory-list-toolbar menu-toolbar">
        <div className="menu-toolbar-row inventory-supplier-toolbar-row">
          <div className="menu-food-filter-wrap">
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              className="menu-food-filter-input"
              placeholder="Search suppliers…"
              value={supplierListSearch}
              onChange={(e) => setSupplierListSearch(e.target.value)}
              aria-label="Search suppliers"
              role="searchbox"
            />
            {supplierListSearch.trim() !== '' && (
              <button
                type="button"
                className="menu-food-filter-clear"
                aria-label="Clear search"
                onClick={() => setSupplierListSearch('')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
          <div className="menu-toolbar-spacer" />
          <div className="purchase-toolbar-actions">
            <button type="button" className="purchase-btn purchase-btn--create" onClick={handleOpen}>
              Add Supplier
            </button>
          </div>
        </div>
      </div>

      <div className="menu-table-wrapper">
        <table className="menu-table admin-rows-table">
          <thead>
            <tr>
              <OrdersSortableTh label="Name" sortKey="supplierName" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Contact Person" sortKey="contactPerson" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Mobile#" sortKey="mobile" className="table-col-mobile" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Email" sortKey="email" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Alternate Number#" sortKey="altNo" className="table-col-mobile" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Address" sortKey="address" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <OrdersSortableTh label="Status" sortKey="isActive" activeSortKey={suppliersSort?.key} sortDirection={suppliersSort?.direction} onSortAsc={handleSuppliersSortAsc} onSortDesc={handleSuppliersSortDesc} />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliersLoading ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  Loading suppliers...
                </td>
              </tr>
            ) : suppliers.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  No suppliers yet.
                </td>
              </tr>
            ) : sortedSuppliers.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  {supplierStatFilter !== 'all' || supplierListSearchQuery
                    ? 'No suppliers match your filters.'
                    : 'No suppliers match your search.'}
                </td>
              </tr>
            ) : (
              pagedSuppliers.map((s) => {
                const sid = s.supplierId ?? s.Supplier_Id;
                const name = s.supplierName ?? s.Supplier_Name ?? '—';
                const mobile = s.mobile ?? s.Mobile;
                const email = s.email ?? s.Email;
                const address = s.address ?? s.Address;
                const active = isSupplierActive(s);
                const detailPath = sid != null ? `/supplier/${encodeURIComponent(sid)}` : null;
                const openDetail = detailPath ? () => navigate(detailPath) : undefined;
                return (
                  <tr
                    key={sid ?? `${s.supplierName}-${s.mobile}`}
                    className={detailPath ? 'eats-admin-row--clickable' : undefined}
                    onClick={rowClickHandler(openDetail)}
                  >
                    <td>
                      <AdminNameCell
                        name={name}
                        id={sid}
                        status={active ? 'ACTIVE' : 'INACTIVE'}
                        to={detailPath || undefined}
                      />
                    </td>
                    <td>{s.contactPerson ?? s.Contact_Person ?? '—'}</td>
                    <td className="table-col-mobile">{formatMobileDisplayOrFallback(mobile, '—')}</td>
                    <td>{email || '—'}</td>
                    <td className="table-col-mobile">{formatMobileDisplayOrFallback(s.altNo ?? s.Alt_No, '—')}</td>
                    <td className="inventory-comments-cell" title={address ?? ''}>
                      {address != null && String(address).trim() !== '' ? String(address) : '—'}
                    </td>
                    <td>{active ? 'Active' : 'Inactive'}</td>
                    <td className="menu-table-actions-cell">
                      <AdminRowActions
                        name={name}
                        phone={mobile}
                        email={email}
                        onView={openDetail}
                        onEdit={() => handleEdit(s)}
                        onDeactivate={active ? () => handleDelete(sid) : undefined}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!suppliersLoading && sortedSuppliers.length > 0 ? (
        <OrdersTablePagination
          currentPage={suppliersPageSafe}
          totalPages={suppliersTotalPages}
          totalItems={sortedSuppliers.length}
          pageSize={suppliersPageSize}
          onPageChange={handleSuppliersPageChange}
          onPageSizeChange={handleSuppliersPageSizeChange}
          itemLabel="suppliers"
          ariaLabel="Suppliers table pagination"
        />
      ) : null}
      </div>

      {showModal && (
        <div className="inventory-modal-overlay">
          <div className="inventory-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="supplier-modal-title">
            <div className="inventory-modal-header">
              <h4 id="supplier-modal-title">{editingSupplierId ? 'Update Supplier' : 'Add Supplier'}</h4>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={handleClose}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {supplierDetailLoading && editingSupplierId ? (
              <p className="inventory-detail-loading-hint">Loading latest details…</p>
            ) : null}

            {submitError ? <p className="inventory-status">{submitError}</p> : null}

            <form className="inventory-form inventory-form--supplier-modal" onSubmit={handleSubmit}>
              <div className="inventory-form-group">
                <label>Supplier name</label>
                <input
                  type="text"
                  name="supplierName"
                  className="add-menu-form-field"
                  value={form.supplierName}
                  onChange={handleChange}
                  placeholder="e.g. Sindhu Traders"
                  required
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label>Contact person</label>
                <input
                  type="text"
                  name="contactPerson"
                  className="add-menu-form-field"
                  value={form.contactPerson}
                  onChange={handleChange}
                  placeholder="Contact name"
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  className="add-menu-form-field"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label>Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  className="add-menu-form-field"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  required
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label>Alternate number#</label>
                <input
                  type="tel"
                  name="altNo"
                  className="add-menu-form-field"
                  value={form.altNo}
                  onChange={handleChange}
                  placeholder="10-digit number (optional)"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              <div className="inventory-form-group inventory-supplier-address-group">
                <label htmlFor="supplier-address">Address</label>
                <AddressSuggestInput
                  id="supplier-address"
                  value={form.address}
                  onChange={(next) => setForm((prev) => ({ ...prev, address: next }))}
                  placeholder="Search address…"
                  inputClassName="add-menu-form-field"
                  disabled={!!editingSupplierId && supplierDetailLoading}
                />
              </div>
              {editingSupplierId && !editStartedActive && (
                <div className="inventory-form-group inventory-form-group--checkbox">
                  <label className="inventory-checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleChange}
                      disabled={!!editingSupplierId && supplierDetailLoading}
                    />
                    <span>Active</span>
                  </label>
                </div>
              )}
              <div className="inventory-form-actions inventory-supplier-form-actions">
                <button
                  type="submit"
                  className="add-button"
                  disabled={isSubmitting || (!!editingSupplierId && supplierDetailLoading)}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySupplier;
